import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ImageButtons } from 'components/Field/ImageButtons';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { UserAvatar } from 'components/UserAvatar';
import { useConfirm } from 'hooks/useConfirm';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { SIGN_OUT } from 'store/auth/actions';
import { selectUser } from 'store/auth/selectors';
import {
  REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD,
  REMOTE_ACCOUNT_LOAD_DETAILS,
  REMOTE_ACCOUNT_LOAD_TFA,
  REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD,
  REMOTE_ACCOUNT_PASSWORD_DELETE,
  REMOTE_ACCOUNT_SAVE_AVATAR,
} from 'store/remote/actions';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SETTINGS_PROFILE
>;

export const ProfileScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const confirm = useConfirm();
  const user = useSelector(selectUser);
  const handleGoPasswordless = () => {
    confirm({
      title: 'Go Passwordless!',
      confirmText: 'Go!',
      titleIcon: ICONS.PartyHornLight,
      text: 'Instead of remembering a password, RiskBase emails you a 6 digit code when you sign in',
      onConfirm: () => dispatch(REMOTE_ACCOUNT_PASSWORD_DELETE()),
    });
  };

  if (!user) {
    return null;
  }

  return (
    <PageScrollView
      isInModal={false}
      contentPaddingTop={0}
      header={
        <NavigationHeader
          heading="Profile"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      <View
        style={{
          alignItems: 'center',
          flex: 1,
          flexDirection: 'column',
          paddingHorizontal: theme.paddingHorizontal,
          paddingVertical: theme.paddingVertical,
        }}
      >
        <UserAvatar size={200} />
        <View
          style={{
            alignItems: 'center',
            flexDirection: 'row',
            marginTop: -theme.grid * 2,
          }}
        >
          <ImageButtons
            enableRemoveButton={!!user.avatar}
            removeConfirmMessage="Are you sure you want to remove your avatar?"
            action={REMOTE_ACCOUNT_SAVE_AVATAR({ image: null })}
            shadow
          />
        </View>
      </View>

      <Card>
        <CardRow
          label="Details"
          value={user.name}
          onPress={() => dispatch(REMOTE_ACCOUNT_LOAD_DETAILS())}
        />
        <CardRow
          label="Email"
          value={user.email}
          onPress={() => dispatch(REMOTE_ACCOUNT_EMAIL_CHANGE_LOAD())}
        />
      </Card>

      {user.ofa === 'password' && (
        <Card
          bodyStyle={{
            borderColor: theme.colors.blue,
            borderWidth: 1,
            marginBottom: theme.paddingHorizontal / 2,
          }}
          margin={false}
        >
          <CardRow
            icon={ICONS.PartyHornLight}
            label="Go Passwordless!"
            onPress={handleGoPasswordless}
          />
        </Card>
      )}

      {user.ofa !== 'password' && (
        <Card explanation="RiskBase emails you a 6 digit code when you sign in">
          <CardRow label="Password" value="No password required" />
        </Card>
      )}
      <Card
        explanation={
          !user.tfa
            ? 'Setup multi-factor authentication to add an extra level of security to your account'
            : 'Multi-Factor authentication is setup on your RiskBase account'
        }
      >
        {user.ofa === 'password' && (
          <CardRow
            label="Password"
            value="Change..."
            onPress={() => dispatch(REMOTE_ACCOUNT_PASSWORD_CHANGE_LOAD())}
          />
        )}
        <CardRow
          label="Multi-Factor Authentication"
          value={user.tfa ? 'See details...' : 'Setup now...'}
          onPress={() => dispatch(REMOTE_ACCOUNT_LOAD_TFA())}
        />
      </Card>
      <Card>
        <CardRow
          label="Sign Out"
          icon={ICONS.PowerOffLight}
          iconColor={theme.colors.red}
          labelColor={theme.colors.red}
          onPress={() => dispatch(SIGN_OUT.request())}
        />
      </Card>
    </PageScrollView>
  );
};
